/**
 * The one way the app talks to the API. Same origin, JSON, the session cookie travels on
 * its own. Every non-2xx answer becomes an ApiError carrying the ProblemDetails the API
 * writes, so a screen can show the API's own title and detail.
 */

export interface ProblemDetails {
    status?: number;
    title?: string;
    detail?: string;
    errors?: Record<string, string[]>;
}

export class ApiError extends Error {
    readonly status: number;
    readonly problem: ProblemDetails;

    constructor(status: number, problem: ProblemDetails) {
        super(problem.title ?? `Request failed with ${status}`);
        this.name = 'ApiError';
        this.status = status;
        this.problem = problem;
    }
}

/**
 * Called when a request that expected a session gets a 401. Set once by main.tsx to send
 * the person back to the login screen as "session expired"; requests made by the login
 * screen itself pass `expectsSession: false` so a wrong password does not trigger it.
 */
let onUnauthorized: (() => void) | undefined;
export function setUnauthorizedHandler(handler: () => void) {
    onUnauthorized = handler;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
    body?: unknown;
    expectsSession?: boolean;
}

export async function api<T = void>(path: string, options: RequestOptions = {}): Promise<T> {
    const { body, expectsSession = true, headers, ...init } = options;
    // A FormData body (a file upload) goes as it is; the browser writes the multipart boundary.
    const form = body instanceof FormData;
    const response = await fetch(path, {
        ...init,
        credentials: 'same-origin',
        headers: {
            Accept: 'application/json',
            ...(body !== undefined && !form ? { 'Content-Type': 'application/json' } : {}),
            ...headers,
        },
        body: form ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
        const problem = await readProblem(response);
        if (response.status === 401 && expectsSession) onUnauthorized?.();
        throw new ApiError(response.status, problem);
    }

    if (response.status === 204 || response.status === 202) return undefined as T;
    const data: unknown = await response.json();
    return data as T;
}

async function readProblem(response: Response): Promise<ProblemDetails> {
    try {
        const parsed: unknown = await response.json();
        if (parsed && typeof parsed === 'object') return parsed;
    } catch {
        // not JSON: fall through to the status line
    }
    return { status: response.status, title: response.statusText || undefined };
}
